package org.ssoft.common.ui.wheel

import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.drawscope.*
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.TextMeasurer
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.sp
import api.ResourceType
import apps.App
import io.github.alexzhirkevich.qrose.QrCodePainter
import io.github.alexzhirkevich.qrose.options.QrBrush
import io.github.alexzhirkevich.qrose.options.QrColors
import io.github.alexzhirkevich.qrose.options.QrOptions
import io.github.alexzhirkevich.qrose.options.QrPixelShape
import io.github.alexzhirkevich.qrose.options.QrShapes
import io.github.alexzhirkevich.qrose.options.roundCorners
import io.github.alexzhirkevich.qrose.options.solid
import kotlin.math.roundToInt

internal class InternalWheelResultGenerator(
    private val loadSvg: suspend (id: Long) -> Painter?,
    private val loadWebp: suspend (id: Long) -> ImageBitmap?,
    private val display: Pair<Int, Int> = 1920 to 1080
) : AppWheelFactory.WheelResultGenerator {

    override suspend fun generate(
        fontResolver: FontFamily.Resolver,
        item: App.Wheel.Item,
        code: String,
        isLand: Boolean,
    ): ImageBitmap {
        val screenSize = if (isLand) {
            IntSize(
                width = maxOf(display.first, display.second),
                height = minOf(display.first, display.second)
            )
        } else {
            IntSize(
                width = minOf(display.first, display.second),
                height = maxOf(display.first, display.second)
            )
        }


        val image = ImageBitmap(screenSize.width, screenSize.height)
        val accFontSize = minOf(screenSize.width, screenSize.height).toFloat() * 0.95F
        val accFontSizeH = accFontSize * 0.5F
        val accPadding = minOf(screenSize.width, screenSize.height).toFloat() * 0.05F

        val textMeasurer = TextMeasurer(
            defaultFontFamilyResolver = fontResolver,
            defaultDensity = Density(1F),
            defaultLayoutDirection = LayoutDirection.Ltr,
            cacheSize = 1
        )

        var measuredCode: TextLayoutResult
        var measuredLabel: TextLayoutResult
        var codeMultiply = 0F
        var labelMultiply = 0F

        do {
            measuredCode = textMeasurer.measure(
                text = code,
                style = TextStyle(
                    fontSize = ((accFontSizeH / (20F + codeMultiply))).sp,
                    color = Color(item.color.onSurface),
                    textAlign = TextAlign.Center,
                    fontWeight = FontWeight.Bold
                ),
                maxLines = 1
            )
            codeMultiply += 1
        } while (measuredCode.size.width > accFontSizeH)

        do {
            measuredLabel = textMeasurer.measure(
                text = item.name,
                style = TextStyle(
                    fontSize = ((accFontSize / (5F + labelMultiply))).sp,
                    color = Color(item.color.onSurface),
                    textAlign = TextAlign.Center
                )
            )
            labelMultiply += 1F
        } while (measuredLabel.size.width > accFontSize || measuredLabel.size.height > (accFontSize / 2F))

        val codePainter = QrCodePainter(
            data = code
        )

        CanvasDrawScope().draw(
            density = Density(1F),
            layoutDirection = LayoutDirection.Ltr,
            canvas = Canvas(image),
            size = Size(image.width.toFloat(), image.height.toFloat())
        ) {
            drawRect(Color(item.color.surface))
            if (item.reward.visual != ResourceType.NO_IMAGE) {
                loadWebp(item.reward.visual)?.also {
                    drawImage(
                        image = it,
                        dstSize = IntSize(
                            width = size.width.roundToInt(),
                            height = size.height.roundToInt()
                        )
                    )
                }

                drawText(
                    textLayoutResult = measuredCode,
                    topLeft = Offset(
                        x = size.width - measuredCode.size.width - accPadding,
                        y = size.height - measuredCode.size.height - accPadding,
                    )
                )
            } else if (isLand) {
                renderLand(item, measuredLabel, accFontSizeH, accPadding, codePainter, measuredCode)
            } else {
                renderPort(item, measuredLabel, accFontSizeH, accPadding, codePainter, measuredCode)
            }
        }


        return image
    }

    private suspend fun DrawScope.renderLand(
        item: App.Wheel.Item,
        measuredLabel: TextLayoutResult,
        accFontSizeH: Float,
        accPadding: Float,
        codePainter: QrCodePainter,
        measuredCode: TextLayoutResult
    ) {
        if (item.name.trim().isNotEmpty() && item.icon.isSet) {
            //label + icon
            drawText(
                textLayoutResult = measuredLabel,
                topLeft = Offset(
                    x = (size.width / 4F) - (measuredLabel.size.width / 2F),
                    y = (size.height / 4F) - (measuredLabel.size.height / 2F),
                )
            )
            translate(
                left = (size.width / 4F) - ((accFontSizeH - (accPadding * 2F)) / 2F),
                top = (size.height * 0.75F) - ((accFontSizeH - (accPadding)) / 2F),
            ) {
                item.icon.webp.takeIf { it != ResourceType.NO_IMAGE }
                    ?.let { loadWebp(it) }
                    ?.also {
                        drawImage(
                            image = it,
                            dstSize = IntSize(
                                width = (accFontSizeH - (accPadding * 2F)).roundToInt(),
                                height = (accFontSizeH - (accPadding * 2F)).roundToInt(),
                            )
                        )
                    }
                item.icon.svg.takeIf { it != ResourceType.NO_ICON }
                    ?.let { loadSvg(it) }
                    ?.apply {
                        draw(
                            Size(
                                width = accFontSizeH - (accPadding * 2F),
                                height = accFontSizeH - (accPadding * 2F)
                            ),
                            colorFilter = ColorFilter.tint(Color(item.color.onSurface))
                        )
                    }
            }

        } else if (item.icon.isSet) {
            //icon
            translate(
                left = (size.width / 4F) - ((accFontSizeH) / 2F),
                top = (size.height * 0.5F) - ((accFontSizeH) / 2F),
            ) {
                item.icon.webp.takeIf { it != ResourceType.NO_IMAGE }
                    ?.let { loadWebp(it) }
                    ?.also {
                        drawImage(it, dstSize = IntSize(accFontSizeH.roundToInt(), accFontSizeH.roundToInt()))
                    }
                item.icon.svg.takeIf { it != ResourceType.NO_ICON }
                    ?.let { loadSvg(it) }
                    ?.apply {
                        draw(
                            Size(accFontSizeH, accFontSizeH),
                            colorFilter = ColorFilter.tint(Color(item.color.onSurface))
                        )
                    }
            }
        } else if (item.name.trim().isNotEmpty()) {
            //label
            drawText(
                textLayoutResult = measuredLabel,
                topLeft = Offset(
                    x = (size.width / 4F) - (measuredLabel.size.width / 2F),
                    y = (size.height / 2F) - (measuredLabel.size.height / 2F),
                )
            )
        } else {
            //NONE
        }
        translate(
            left = (size.width * 0.75F) - (accFontSizeH / 2F) - accPadding,
            top = (size.height / 2F) - (accFontSizeH / 2F) - accPadding,
        ) {
            drawPath(Path().apply {
                addRoundRect(
                    RoundRect(
                        rect = Rect(
                            offset = Offset.Zero,
                            size = Size(
                                accFontSizeH + (accPadding * 2),
                                accFontSizeH + (accPadding * 2)
                            ),
                        ),
                        bottomLeft = CornerRadius(10f, 10f),
                        bottomRight = CornerRadius(10f, 10f),
                        topLeft = CornerRadius(10f, 10f),
                        topRight = CornerRadius(10f, 10f),
                    )
                )
            }, color = Color.White)

            translate(accPadding, accPadding) {
                with(codePainter) {
                    draw(Size(accFontSizeH, accFontSizeH))
                }
            }
        }
        drawText(
            textLayoutResult = measuredCode,
            topLeft = Offset(
                x = (size.width * 0.75F) - (measuredCode.size.width / 2F),
                y = (size.height / 2F) + (accFontSizeH / 2F) + (accPadding * 2),
            )
        )
    }


    private suspend fun DrawScope.renderPort(
        item: App.Wheel.Item,
        measuredLabel: TextLayoutResult,
        accFontSizeH: Float,
        accPadding: Float,
        codePainter: QrCodePainter,
        measuredCode: TextLayoutResult
    ) {
        if (item.name.trim().isNotEmpty() && item.icon.isSet) {
            //label + icon
            drawText(
                textLayoutResult = measuredLabel,
                topLeft = Offset(
                    x = (size.width * 0.5F) - (measuredLabel.size.width / 2F),
                    y = (size.height * 0.125F) - (measuredLabel.size.height / 2F),
                )
            )
            translate(
                left = (size.width * 0.5F) - ((accFontSizeH - (accPadding)) / 2F),
                top = (size.height * 0.375F) - ((accFontSizeH - (accPadding)) / 2F),
            ) {
                item.icon.webp.takeIf { it != ResourceType.NO_IMAGE }
                    ?.let { loadWebp(it) }
                    ?.also {
                        drawImage(
                            image = it,
                            dstSize = IntSize(
                                width = (accFontSizeH - (accPadding * 2F)).roundToInt(),
                                height = (accFontSizeH - (accPadding * 2F)).roundToInt(),
                            )
                        )
                    }
                item.icon.svg.takeIf { it != ResourceType.NO_ICON }
                    ?.let { loadSvg(it) }
                    ?.apply {
                        draw(
                            Size(
                                width = accFontSizeH - (accPadding * 2F),
                                height = accFontSizeH - (accPadding * 2F)
                            ),
                            colorFilter = ColorFilter.tint(Color(item.color.onSurface))
                        )
                    }
            }

        } else if (item.icon.isSet) {
            //icon
            translate(
                left = (size.width * 0.5F) - ((accFontSizeH) / 2F),
                top = (size.height * 0.25F) - ((accFontSizeH) / 2F),
            ) {
                item.icon.webp.takeIf { it != ResourceType.NO_IMAGE }
                    ?.let { loadWebp(it) }
                    ?.also {
                        drawImage(it, dstSize = IntSize(accFontSizeH.roundToInt(), accFontSizeH.roundToInt()))
                    }
                item.icon.svg.takeIf { it != ResourceType.NO_ICON }
                    ?.let { loadSvg(it) }
                    ?.apply {
                        draw(
                            Size(accFontSizeH, accFontSizeH),
                            colorFilter = ColorFilter.tint(Color(item.color.onSurface))
                        )
                    }
            }
        } else if (item.name.trim().isNotEmpty()) {
            //label
            drawText(
                textLayoutResult = measuredLabel,
                topLeft = Offset(
                    x = (size.width * 0.5F) - (measuredLabel.size.width / 2F),
                    y = (size.height * 0.25F) - (measuredLabel.size.height / 2F),
                )
            )
        } else {
            //NONE
        }
        translate(
            left = (size.width * 0.5F) - (accFontSizeH / 2F) - accPadding,
            top = (size.height * 0.75F) - (accFontSizeH / 2F) - accPadding,
        ) {
            drawPath(Path().apply {
                addRoundRect(
                    RoundRect(
                        rect = Rect(
                            offset = Offset.Zero,
                            size = Size(
                                accFontSizeH + (accPadding * 2),
                                accFontSizeH + (accPadding * 2)
                            ),
                        ),
                        bottomLeft = CornerRadius(10f, 10f),
                        bottomRight = CornerRadius(10f, 10f),
                        topLeft = CornerRadius(10f, 10f),
                        topRight = CornerRadius(10f, 10f),
                    )
                )
            }, color = Color.White)

            translate(accPadding, accPadding) {
                with(codePainter) {
                    draw(Size(accFontSizeH, accFontSizeH))
                }
            }
        }
        drawText(
            textLayoutResult = measuredCode,
            topLeft = Offset(
                x = (size.width * 0.5F) - (measuredCode.size.width / 2F),
                y = (size.height * 0.75F) + (accFontSizeH / 2F) + (accPadding * 2),
            )
        )
    }
}

private val App.Wheel.Icon.isSet get() = svg != ResourceType.NO_ICON || webp != ResourceType.NO_IMAGE